import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/page-header";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Section, Field, ActionBar } from "@/components/form-shell";

const STAGE_BY_PIPELINE: Record<string, string[]> = {
  CONSULTING: ["QUALIFYING", "DISCOVERY", "PROPOSAL", "NEGOTIATION", "CLOSED_WON", "CLOSED_LOST"],
  PRODUCT:    ["QUALIFYING", "DISCOVERY", "PROPOSAL", "NEGOTIATION", "CLOSED_WON", "CLOSED_LOST"],
  STAFFING:   ["REQUISITION", "CANDIDATE_SOURCING", "SUBMITTED_TO_CLIENT", "INTERVIEW", "OFFER", "PLACED", "CLOSED_LOST"],
  GOVERNMENT: ["MARKET_RESEARCH", "CAPTURE", "PROPOSAL_DEV", "SUBMITTED", "AWARDED", "LOST"],
};

const EMPTY = {
  name: "", pipelineType: "CONSULTING", stage: "QUALIFYING",
  valueUsd: "0", probability: "10", entity: "BOTH",
  accountId: "", contactId: "", closeDate: "",
  type: "", nextStep: "", leadSource: "", campaignId: "", description: "",
  forecastCategory: "", territory: "",
};
type DealForm = typeof EMPTY;

export default function DealNewPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [accounts, setAccounts]   = useState<{ id: string; name: string }[]>([]);
  const [contacts, setContacts]   = useState<{ id: string; name: string }[]>([]);
  const [campaigns, setCampaigns] = useState<{ id: string; name: string }[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<DealForm>(EMPTY);

  useEffect(() => {
    api.get("/accounts",  { params: { pageSize: 100, sortBy: "name", sortDir: "asc" } }).then((r) => setAccounts(r.data.data ?? []));
    api.get("/campaigns").then((r) => setCampaigns(r.data.data ?? []));
  }, []);

  // Contact picker — scoped to selected account if any.
  useEffect(() => {
    const params: Record<string, unknown> = { pageSize: 100, sortBy: "name", sortDir: "asc" };
    if (form.accountId) params.accountId = form.accountId;
    api.get("/contacts", { params }).then((r) => setContacts(r.data.data ?? []));
  }, [form.accountId]);

  useEffect(() => {
    // Reset stage when pipeline changes.
    setForm((f) => ({ ...f, stage: STAGE_BY_PIPELINE[f.pipelineType][0] ?? f.stage }));
  }, [form.pipelineType]);

  const expectedRevenue = useMemo(() => {
    const v = Number(form.valueUsd) || 0;
    const p = Number(form.probability) || 0;
    return v * (p / 100);
  }, [form.valueUsd, form.probability]);

  function set<K extends keyof DealForm>(k: K, v: DealForm[K]) { setForm((f) => ({ ...f, [k]: v })); }

  async function save(mode: "save" | "saveAndNew") {
    setBusy(true); setError(null);
    try {
      const payload: any = {};
      for (const [k, v] of Object.entries(form)) {
        if (v === "" || v === null || v === undefined) continue;
        if (k === "valueUsd")    payload.valueUsd = Number(v);
        else if (k === "probability") payload.probability = Number(v);
        else payload[k] = v;
      }
      await api.post("/deals", payload);
      qc.invalidateQueries({ queryKey: ["/deals"] });
      qc.invalidateQueries({ queryKey: ["deals-kanban"] });
      if (mode === "saveAndNew") setForm(EMPTY);
      else navigate("/deals");
    } catch (err: any) {
      setError(err?.response?.data?.message ?? "Failed to create deal.");
    } finally { setBusy(false); }
  }

  const stages = STAGE_BY_PIPELINE[form.pipelineType] ?? [];

  return (
    <>
      <PageHeader title="Create Deal" description="Add an opportunity to a pipeline." />
      <form onSubmit={(e) => { e.preventDefault(); save("save"); }} className="p-6">
        <div className="mx-auto max-w-4xl space-y-4">
          <Section title="Deal Information">
            <Field id="name" label="Deal Name" required>
              <Input id="name" required value={form.name} onChange={(e) => set("name", e.target.value)} />
            </Field>
            <Field id="valueUsd" label="Amount (USD)">
              <Input id="valueUsd" type="number" min="0" value={form.valueUsd} onChange={(e) => set("valueUsd", e.target.value)} />
            </Field>
            <Field id="accountId" label="Account" required>
              <Select id="accountId" value={form.accountId} onChange={(e) => set("accountId", e.target.value)} required>
                <option value="">— Select account —</option>
                {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </Select>
            </Field>
            <Field id="closeDate" label="Closing Date">
              <Input id="closeDate" type="date" value={form.closeDate} onChange={(e) => set("closeDate", e.target.value)} />
            </Field>
            <Field id="contactId" label="Contact">
              <Select id="contactId" value={form.contactId} onChange={(e) => set("contactId", e.target.value)}>
                <option value="">—</option>
                {contacts.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </Select>
            </Field>
            <Field id="pipelineType" label="Pipeline">
              <Select id="pipelineType" value={form.pipelineType} onChange={(e) => set("pipelineType", e.target.value)}>
                <option value="CONSULTING">Consulting</option>
                <option value="PRODUCT">Products</option>
                <option value="STAFFING">Staffing</option>
                <option value="GOVERNMENT">Government</option>
              </Select>
            </Field>
            <Field id="type" label="Type">
              <Select id="type" value={form.type} onChange={(e) => set("type", e.target.value)}>
                <option value="">—</option>
                <option value="New Business">New Business</option>
                <option value="Existing — Upgrade">Existing — Upgrade</option>
                <option value="Existing — Renewal">Existing — Renewal</option>
                <option value="Existing — Cross-sell">Existing — Cross-sell</option>
              </Select>
            </Field>
            <Field id="stage" label="Stage">
              <Select id="stage" value={form.stage} onChange={(e) => set("stage", e.target.value)}>
                {stages.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
              </Select>
            </Field>
            <Field id="probability" label="Probability (%)">
              <Input id="probability" type="number" min="0" max="100" value={form.probability} onChange={(e) => set("probability", e.target.value)} />
            </Field>
            <Field id="expectedRevenue" label="Expected Revenue">
              <Input id="expectedRevenue" readOnly value={`$${Math.round(expectedRevenue).toLocaleString()}`} className="bg-muted" />
            </Field>
            <Field id="nextStep" label="Next Step" className="md:col-span-2">
              <Input id="nextStep" placeholder="Send proposal by Friday, schedule exec review…" value={form.nextStep} onChange={(e) => set("nextStep", e.target.value)} />
            </Field>
            <Field id="leadSource" label="Lead Source">
              <Select id="leadSource" value={form.leadSource} onChange={(e) => set("leadSource", e.target.value)}>
                <option value="">—</option>
                <option value="Cold Call">Cold Call</option>
                <option value="LinkedIn">LinkedIn</option>
                <option value="Apollo">Apollo</option>
                <option value="Webinar">Webinar</option>
                <option value="Event">Event</option>
                <option value="Partner">Partner</option>
                <option value="Referral">Referral</option>
                <option value="Email Campaign">Email Campaign</option>
              </Select>
            </Field>
            <Field id="campaignId" label="Campaign Source">
              <Select id="campaignId" value={form.campaignId} onChange={(e) => set("campaignId", e.target.value)}>
                <option value="">—</option>
                {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </Select>
            </Field>
            <Field id="entity" label="Entity">
              <Select id="entity" value={form.entity} onChange={(e) => set("entity", e.target.value)}>
                <option value="BOTH">Both</option>
                <option value="US">US</option>
                <option value="INDIA">India</option>
                <option value="GLOCALIZE">Glocalize</option>
              </Select>
            </Field>
          </Section>

          <Section title="Forecast">
            <Field id="forecastCategory" label="Forecast category">
              <Select id="forecastCategory" value={form.forecastCategory} onChange={(e) => set("forecastCategory", e.target.value)}>
                <option value="">— Not set —</option>
                <option value="Commit">Commit</option>
                <option value="Best Case">Best Case</option>
                <option value="Pipeline">Pipeline</option>
                <option value="Omitted">Omitted</option>
                <option value="Closed">Closed</option>
              </Select>
            </Field>
            <Field id="territory" label="Territory">
              <Input id="territory" placeholder="NA-East, APAC-South…" value={form.territory} onChange={(e) => set("territory", e.target.value)} />
            </Field>
          </Section>

          <Section title="Description">
            <div className="md:col-span-2">
              <textarea
                id="description"
                className="flex min-h-[100px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                placeholder="Context, notes, internal detail…"
                value={form.description}
                onChange={(e) => set("description", e.target.value)}
              />
            </div>
          </Section>

          <ActionBar
            busy={busy}
            canSubmit={!!form.name && !!form.accountId}
            onCancel={() => navigate("/deals")}
            onSaveAndNew={() => save("saveAndNew")}
            onSubmit={() => save("save")}
            error={error}
          />
        </div>
      </form>
    </>
  );
}
